import type { IStatsRepository } from "@src/domain/stats/repository/stats_repository";
import { Stats } from "@src/domain/stats/value_objects/stats";
import type { PascoStats, DashboardStats, PascoJobStats } from "@src/domain/stats/value_objects/stats";
import type { ApiClient } from "../api/client";

/**
 * Stats Repository Implementation
 * Implements the IStatsRepository interface using HTTP API calls
 * 
 * This implementation translates raw API data into domain value objects,
 * following Clean Architecture principles.
 */
export class StatsRepositoryImpl implements IStatsRepository {
    private readonly apiClient: ApiClient;

    constructor(apiClient: ApiClient) {
        this.apiClient = apiClient;
    }

    async getStats(): Promise<Stats> {
        const data = await this.apiClient.get<any>("/stats");

        // Transform the API response to domain value object
        const pasco: PascoStats = {
            totalPascos: data.pasco.total_pascos,
            totalQuestionChunks: data.pasco.total_question_chunks,
            statusBreakdown: data.pasco.status_breakdown || {},
            pendingApproval: data.pasco.pending_approval || 0,
            approved: data.pasco.approved || 0,
        };

        const dashboard: DashboardStats = {
            totalUsers: data.dashboard.total_users,
            activeUsers: data.dashboard.active_users,
            bannedUsers: data.dashboard.banned_users,
            verifiedUsers: data.dashboard.verified_users,
            usersWithRoles: data.dashboard.users_with_roles,
            usersWithoutRoles: data.dashboard.users_without_roles,
            totalRoles: data.dashboard.total_roles,
            activeRoles: data.dashboard.active_roles,
            totalPermissions: data.dashboard.total_permissions,
        };

        const pascoJobs: PascoJobStats = {
            totalJobs: data.pasco_jobs?.total_jobs || 0,
            statusBreakdown: data.pasco_jobs?.status_breakdown || {},
        };

        return Stats.create({
            pasco,
            dashboard,
            pascoJobs,
            timestamp: new Date(data.timestamp),
        });
    }
}
